/**
 * Custom error classes for better error handling
 */

/**
 * Detailed error info from backend for each failed operation
 */
export interface PartialFailureErrorDetail {
  quote_id: string;
  error: string;
  requires_schedule?: boolean;
  requires_channel?: boolean;
}

/**
 * Error thrown when a bulk operation partially fails
 * Preserves the list of failed IDs and detailed error reasons for selective rollback
 */
export class PartialFailureError extends Error {
  public readonly failedIds: string[];
  public readonly successCount: number;
  public readonly failedCount: number;
  /** Detailed error info for each failed operation */
  public readonly errors: PartialFailureErrorDetail[];
  /** True if failure was due to missing schedule pattern */
  public readonly requiresSchedule: boolean;
  /** True if failure was due to missing YouTube channel */
  public readonly requiresChannel: boolean;

  constructor(
    failedIds: string[],
    successCount: number,
    errors: PartialFailureErrorDetail[] = [],
    requiresSchedule = false,
    requiresChannel = false
  ) {
    const failedCount = failedIds.length;
    const totalCount = failedCount + successCount;
    super(`Partial failure: ${failedCount} of ${totalCount} operations failed`);

    this.name = 'PartialFailureError';
    this.failedIds = failedIds;
    this.successCount = successCount;
    this.failedCount = failedCount;
    this.errors = errors;
    this.requiresSchedule = requiresSchedule;
    this.requiresChannel = requiresChannel;

    // Maintains proper stack trace for where our error was thrown (only in V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, PartialFailureError);
    }
  }
}

/**
 * Error thrown when all operations in a bulk request fail
 */
export class AllFailedError extends Error {
  public readonly failedIds: string[];

  constructor(failedIds: string[]) {
    super(`All operations failed. Failed IDs: ${failedIds.join(', ')}`);
    this.name = 'AllFailedError';
    this.failedIds = failedIds;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AllFailedError);
    }
  }
}

/**
 * Error thrown when user doesn't have enough credits for an operation
 */
export class InsufficientCreditsError extends Error {
  public readonly creditsNeeded: number;
  public readonly creditsAvailable: number;

  constructor(creditsNeeded: number, creditsAvailable: number, message?: string) {
    super(message || `Insufficient credits: need ${creditsNeeded}, have ${creditsAvailable}`);
    this.name = 'InsufficientCreditsError';
    this.creditsNeeded = creditsNeeded;
    this.creditsAvailable = creditsAvailable;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, InsufficientCreditsError);
    }
  }
}
